import { useCallback, useEffect, useState } from 'react'
import { ChefHat, Ellipsis, SquarePen } from 'lucide-react'
import type { AppState, HealthResponse, OnboardingStep, Recipe } from '../shared/types.ts'
import { api } from './lib/api.ts'
import { CookMenu } from './components/CookMenu.tsx'
import { MenuSheet, type MenuKey } from './components/MenuSheet.tsx'
import { Onboarding } from './components/Onboarding.tsx'
import { PantrySheet } from './components/PantrySheet.tsx'
import { RecipeSheet } from './components/RecipeSheet.tsx'
import { SavedSheet } from './components/SavedSheet.tsx'
import { SettingsSheet } from './components/SettingsSheet.tsx'
import { SetupSheet } from './components/SetupSheet.tsx'

export default function App() {
  const [state, setState] = useState<AppState | null>(null)
  const [health, setHealth] = useState<HealthResponse | null>(null)
  const [loadError, setLoadError] = useState('')
  const [sheet, setSheet] = useState<MenuKey | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [recipe, setRecipe] = useState<Recipe | null>(null)
  const [toast, setToast] = useState('')
  // Seeded from the store, then owned locally: finishing a step writes completedAt
  // immediately, but the cook should read the closing line before the view changes.
  const [setupOpen, setSetupOpen] = useState<boolean | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        const [s, h] = await Promise.all([api.state(), api.health().catch(() => null)])
        setState(s)
        setHealth(h)
        setSetupOpen(!s.onboarding.completedAt)
      } catch (e) {
        setLoadError((e as Error).message)
      }
    })()
  }, [])

  const say = useCallback((message: string) => {
    setToast(message)
    setTimeout(() => setToast((t) => (t === message ? '' : t)), 1900)
  }, [])

  if (loadError) {
    return (
      <div className="boot">
        <h2>Can’t reach the server</h2>
        <p>{loadError}</p>
        <p className="muted small">Start it with <code>npm run dev</code> from the project folder.</p>
      </div>
    )
  }
  if (!state || setupOpen === null) return <div className="boot"><span className="spinner" /></div>

  /*
   * Accepts an updater as well as a plain patch. A streamed turn lands in two events
   * seconds apart, and the second one must build on the first — reading `state` from
   * the render closure would silently write the older conversation back.
   */
  const patch = (p: Partial<AppState> | ((s: AppState) => Partial<AppState>)) =>
    setState((s) => (s ? { ...s, ...(typeof p === 'function' ? p(s) : p) } : s))
  const keysReady = Boolean(health?.perplexity)
  const savedIds = new Set(state.saved.map((s) => s.recipe.id))

  const redo = async (step: OnboardingStep) => {
    setSheet(null)
    setState(await api.reopen(step))
    setSetupOpen(true)
  }

  /*
   * Clearing is the only way a conversation ever goes away — there is no history list
   * to recover it from — so it asks first, and only when there is something to lose.
   */
  const newChat = async () => {
    if (!confirm('Start a new chat? Tonight’s menu will be cleared. Saved recipes are kept.')) return
    patch({ conversation: (await api.clearChat()).conversation })
    say('New chat')
  }

  const toggleSave = async (r: Recipe) => {
    const res = savedIds.has(r.id) ? await api.unsaveRecipe(r.id) : await api.saveRecipe(r, '')
    patch({ saved: res.saved })
    say(savedIds.has(r.id) ? 'Removed from saved' : 'Saved')
  }

  const notes: Record<MenuKey, string> = {
    setup: 'kitchen, limits, taste',
    pantry: state.pantry.length ? `${state.pantry.length} tracked` : 'optional',
    saved: state.saved.length ? `${state.saved.length} kept` : 'nothing yet',
    settings: 'models and data',
  }

  return (
    <div className="app">
      <header className="topbar">
        <span className="mark"><ChefHat size={24} strokeWidth={2.4} /></span>
        <b className="brand">Let Them Cook</b>
        {!setupOpen && state.conversation.length > 0 && (
          <button className="icon-btn" aria-label="Start a new chat" title="Start a new chat" onClick={() => void newChat()}>
            <SquarePen size={19} strokeWidth={2.6} />
          </button>
        )}
        {!setupOpen && (
          <button className="icon-btn" aria-label="Menu" onClick={() => setMenuOpen(true)}>
            <Ellipsis size={20} strokeWidth={2.8} />
          </button>
        )}
      </header>

      <main>
        {setupOpen ? (
          <Onboarding
            state={state}
            onState={setState}
            onDone={() => setSetupOpen(false)}
            canType={Boolean(health?.inception)}
          />
        ) : (
          <CookMenu state={state} onState={patch} keysReady={keysReady} onOpenRecipe={setRecipe} />
        )}
      </main>

      {menuOpen && (
        <MenuSheet notes={notes} onClose={() => setMenuOpen(false)} onPick={(key) => { setSheet(key); setMenuOpen(false) }} />
      )}

      {sheet === 'setup' && <SetupSheet state={state} onState={patch} onClose={() => setSheet(null)} onRedo={redo} />}
      {sheet === 'pantry' && (
        <PantrySheet
          pantry={state.pantry}
          onPantry={(pantry) => patch({ pantry })}
          onClose={() => setSheet(null)}
          canType={Boolean(health?.inception)}
        />
      )}
      {sheet === 'saved' && (
        <SavedSheet saved={state.saved} onClose={() => setSheet(null)} onOpen={(r) => { setSheet(null); setRecipe(r) }} />
      )}
      {sheet === 'settings' && (
        <SettingsSheet
          settings={state.settings}
          onSettings={(settings) => patch({ settings })}
          health={health}
          onClose={() => setSheet(null)}
        />
      )}
      {recipe && (
        <RecipeSheet
          recipe={recipe}
          saved={savedIds.has(recipe.id)}
          onClose={() => setRecipe(null)}
          onToggleSave={() => void toggleSave(recipe)}
        />
      )}

      {toast && <div className="toast" role="status">{toast}</div>}
    </div>
  )
}
